import axios from "axios";

function getOrgId(orgOrId) {
  if (!orgOrId) return null;
  if (typeof orgOrId === "string") return orgOrId;
  const raw = orgOrId.orgId ?? orgOrId.org_id;
  return typeof raw === "string" ? raw : raw?.S ?? null;
}

function readOrganization() {
  try {
    return JSON.parse(localStorage.getItem("organization"));
  } catch {
    return null;
  }
}

/**
 * Read onTwilioTrial from Dynamo-shaped or plain org objects.
 * @returns {boolean|undefined} true/false when known; undefined when unset
 */
export function getOnTwilioTrial(org) {
  if (!org) return undefined;
  const ti = org.twilio_integration;
  if (ti?.M?.onTwilioTrial?.BOOL !== undefined) {
    return ti.M.onTwilioTrial.BOOL;
  }
  if (typeof ti?.onTwilioTrial === "boolean") {
    return ti.onTwilioTrial;
  }
  if (typeof org.onTwilioTrial === "boolean") {
    return org.onTwilioTrial;
  }
  return undefined;
}

/**
 * Merge only onTwilioTrial into the existing org.
 * Never invents twilio_integration, never rewraps plain shapes, never
 * replaces the full org or notifies Main (avoids subscription-gate side effects).
 */
function patchLocalOnTwilioTrial(onTwilioTrial) {
  const org = readOrganization();
  if (!org?.twilio_integration?.M) return;
  org.twilio_integration.M.onTwilioTrial = { BOOL: !!onTwilioTrial };
  localStorage.setItem("organization", JSON.stringify(org));
}

/**
 * If flag is false, skip network. If true or unset, call check-account-type.
 * Fail open: on error return { onTwilioTrial: false, checkFailed: true }.
 */
export async function ensureTrialStatus(orgId) {
  const id = getOrgId(orgId) || getOrgId(readOrganization());
  try {
    const org = readOrganization();
    const flag = getOnTwilioTrial(org);
    if (flag === false) {
      return { onTwilioTrial: false };
    }

    if (!id) {
      return { onTwilioTrial: false, checkFailed: true };
    }

    const res = await axios.post(
      process.env.REACT_APP_BACKEND_URL + "twilio/check-account-type",
      { org_id: id }
    );

    const onTwilioTrial = res.data?.onTwilioTrial === true;
    const accountType = res.data?.accountType;
    patchLocalOnTwilioTrial(onTwilioTrial);

    return { onTwilioTrial, accountType };
  } catch (error) {
    console.error(
      "twilioTrial: check-account-type failed",
      error?.response?.data || error?.message || error
    );
    return { onTwilioTrial: false, checkFailed: true };
  }
}

/**
 * Always call check-account-type (e.g. after credential save). Fail open.
 */
export async function checkAccountType(orgId) {
  const id = getOrgId(orgId) || getOrgId(readOrganization());
  try {
    if (!id) {
      return { onTwilioTrial: false, checkFailed: true };
    }
    const res = await axios.post(
      process.env.REACT_APP_BACKEND_URL + "twilio/check-account-type",
      { org_id: id }
    );
    const onTwilioTrial = res.data?.onTwilioTrial === true;
    const accountType = res.data?.accountType;
    patchLocalOnTwilioTrial(onTwilioTrial);
    return { onTwilioTrial, accountType, updated: res.data?.updated };
  } catch (error) {
    console.error(
      "twilioTrial: check-account-type failed",
      error?.response?.data || error?.message || error
    );
    return { onTwilioTrial: false, checkFailed: true };
  }
}
